"use strict";
var LiftAnalytics = (() => {
  var __defProp = Object.defineProperty;
  var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
  var __getOwnPropNames = Object.getOwnPropertyNames;
  var __hasOwnProp = Object.prototype.hasOwnProperty;
  var __export = (target, all) => {
    for (var name in all)
      __defProp(target, name, { get: all[name], enumerable: true });
  };
  var __copyProps = (to, from, except, desc) => {
    if (from && typeof from === "object" || typeof from === "function") {
      for (let key of __getOwnPropNames(from))
        if (!__hasOwnProp.call(to, key) && key !== except)
          __defProp(to, key, { get: () => from[key], enumerable: !(desc = __getOwnPropDesc(from, key)) || desc.enumerable });
    }
    return to;
  };
  var __toCommonJS = (mod) => __copyProps(__defProp({}, "__esModule", { value: true }), mod);

  // src/index.ts
  var index_exports = {};
  __export(index_exports, {
    calculateVerticalPercentage: () => calculateVerticalPercentage,
    createArticleOutboundClickTracker: () => createArticleOutboundClickTracker,
    createArticleScrollDepthTracker: () => createArticleScrollDepthTracker,
    createArticleTracking: () => createArticleTracking,
    createArticleViewSession: () => createArticleViewSession,
    generateUuid: () => generateUuid,
    stripQueryAndFragment: () => stripQueryAndFragment
  });

  // src/shared/createArticleViewSession.ts
  function generateUuid() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    const cryptoObj = typeof crypto !== "undefined" && crypto || typeof window !== "undefined" && window.msCrypto;
    const bytes = new Uint8Array(16);
    if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
      cryptoObj.getRandomValues(bytes);
    } else {
      for (let i = 0; i < bytes.length; i++) {
        bytes[i] = Math.floor(Math.random() * 256);
      }
    }
    bytes[6] = bytes[6] & 15 | 64;
    bytes[8] = bytes[8] & 63 | 128;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
    return hex.slice(0, 4).join("") + "-" + hex.slice(4, 6).join("") + "-" + hex.slice(6, 8).join("") + "-" + hex.slice(8, 10).join("") + "-" + hex.slice(10, 16).join("");
  }
  function createArticleViewSession() {
    let viewId = null;
    return {
      ensureViewId() {
        if (!viewId) viewId = generateUuid();
        return viewId;
      },
      getViewId() {
        return viewId;
      },
      reset() {
        viewId = null;
      }
    };
  }
  function isArticleViewSession(candidate) {
    return !!candidate && typeof candidate.ensureViewId === "function" && typeof candidate.getViewId === "function" && typeof candidate.reset === "function";
  }

  // src/shared/createConsentGate.ts
  function extractConsentPayload(entry) {
    if (!entry) return null;
    const isArrayLike = Array.isArray(entry) || typeof entry === "object" && typeof entry.length === "number";
    if (isArrayLike) {
      const arrLike = entry;
      if (arrLike[0] === "consent" && arrLike[2] && typeof arrLike[2] === "object") {
        return arrLike[2];
      }
    }
    if (typeof entry === "object" && entry !== null && "consent" in entry && typeof entry.consent === "object") {
      return entry.consent;
    }
    return null;
  }
  function getDataLayerArray() {
    if (typeof window === "undefined") return null;
    const win = window;
    if (!Array.isArray(win.dataLayer)) {
      win.dataLayer = win.dataLayer || [];
    }
    return win.dataLayer;
  }
  function ensureObserved(dataLayer, onEntry) {
    if (!dataLayer) return;
    const existingPush = dataLayer.push;
    if (existingPush && existingPush.__liftConsentListeners) {
      existingPush.__liftConsentListeners.add(onEntry);
      return;
    }
    const originalPush = typeof dataLayer.push === "function" ? dataLayer.push.bind(dataLayer) : Array.prototype.push.bind(dataLayer);
    const listeners = /* @__PURE__ */ new Set([onEntry]);
    function wrappedPush(...args) {
      const result = originalPush(...args);
      args.forEach((entry) => {
        listeners.forEach((fn) => {
          try {
            fn(entry);
          } catch (error) {
          }
        });
      });
      return result;
    }
    wrappedPush.__liftConsentListeners = listeners;
    wrappedPush.__liftOriginalPush = originalPush;
    dataLayer.push = wrappedPush;
  }
  function teardownObserver(dataLayer, onEntry) {
    const push = dataLayer && dataLayer.push;
    if (!push || !push.__liftConsentListeners) return;
    push.__liftConsentListeners.delete(onEntry);
    if (push.__liftConsentListeners.size === 0 && dataLayer && dataLayer.push === push) {
      dataLayer.push = push.__liftOriginalPush;
    }
  }
  function createConsentGate({
    defaultConsentState = "granted",
    onGrantedChange
  } = {}) {
    const consentState = {};
    let bootstrapped = false;
    let observing = false;
    let destroyed = false;
    function onEntry(entry) {
      if (destroyed) return;
      const payload = extractConsentPayload(entry);
      if (payload) onConsentSignal(payload, { historical: false });
    }
    function mergeConsentHistorical(payload) {
      Object.keys(payload).forEach((key) => {
        if (!(key in consentState)) consentState[key] = payload[key];
      });
    }
    function mergeConsentLive(payload) {
      Object.keys(payload).forEach((key) => {
        consentState[key] = payload[key];
      });
    }
    function effectiveAnalyticsStorage() {
      return "analytics_storage" in consentState ? consentState.analytics_storage : defaultConsentState;
    }
    function isGranted() {
      return effectiveAnalyticsStorage() === "granted";
    }
    function onConsentSignal(payload, { historical }) {
      if (destroyed) return;
      const before = isGranted();
      if (historical) {
        mergeConsentHistorical(payload);
      } else {
        mergeConsentLive(payload);
      }
      const after = isGranted();
      if (before !== after && typeof onGrantedChange === "function") {
        onGrantedChange(after);
      }
    }
    function reobserve() {
      if (destroyed) return;
      const dataLayer = getDataLayerArray();
      if (!dataLayer) return;
      ensureObserved(dataLayer, onEntry);
      observing = true;
    }
    function bootstrapOnce() {
      if (bootstrapped || destroyed) return;
      bootstrapped = true;
      const dataLayer = getDataLayerArray();
      if (dataLayer) {
        for (let i = dataLayer.length - 1; i >= 0; i--) {
          const payload = extractConsentPayload(dataLayer[i]);
          if (payload) mergeConsentHistorical(payload);
        }
      }
      reobserve();
    }
    function teardown() {
      if (destroyed) return;
      destroyed = true;
      teardownObserver(getDataLayerArray(), onEntry);
      observing = false;
    }
    return {
      isGranted,
      effectiveAnalyticsStorage,
      getConsentState: () => ({ ...consentState }),
      getDataLayer: getDataLayerArray,
      bootstrapOnce,
      reobserve,
      isObserving: () => observing,
      teardown
    };
  }

  // src/shared/pushDataLayerEvent.ts
  function pushDataLayerEvent({
    dataLayer,
    transport,
    measurementId,
    eventName,
    fields
  }) {
    if (!dataLayer) return;
    if (transport === "gtm") {
      dataLayer.push({ event: eventName, ...fields });
    } else {
      const gtagFields = measurementId ? { ...fields, send_to: measurementId } : fields;
      dataLayer.push(["event", eventName, gtagFields]);
    }
  }

  // src/shared/createActiveInteractionTimer.ts
  var DEFAULT_ACTIVE_IDLE_TIMEOUT_MSEC = 5e3;
  function defaultNow() {
    return typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
  }
  function createActiveInteractionTimer(options = {}) {
    const idleTimeoutMsec = typeof options.idleTimeoutMsec === "number" && options.idleTimeoutMsec > 0 ? options.idleTimeoutMsec : DEFAULT_ACTIVE_IDLE_TIMEOUT_MSEC;
    const now = typeof options.now === "function" ? options.now : defaultNow;
    let attached = false;
    let destroyed = false;
    let visible = true;
    let focused = true;
    let consented = false;
    let withinIdleWindow = false;
    let accumulatedMsec = 0;
    let segmentStartTime = null;
    let idleTimeoutHandle = null;
    function closeOpenSegment() {
      if (segmentStartTime !== null) {
        accumulatedMsec += Math.max(0, now() - segmentStartTime);
        segmentStartTime = null;
      }
    }
    function recomputeCounting() {
      if (destroyed) return;
      const shouldCount = visible && focused && consented && withinIdleWindow;
      if (shouldCount && segmentStartTime === null) {
        segmentStartTime = now();
      } else if (!shouldCount) {
        closeOpenSegment();
      }
    }
    function bumpIdleWindow() {
      if (destroyed) return;
      withinIdleWindow = true;
      if (idleTimeoutHandle !== null) clearTimeout(idleTimeoutHandle);
      idleTimeoutHandle = setTimeout(() => {
        idleTimeoutHandle = null;
        withinIdleWindow = false;
        recomputeCounting();
      }, idleTimeoutMsec);
      recomputeCounting();
    }
    function markActivity() {
      bumpIdleWindow();
    }
    function onVisibilityChange() {
      if (destroyed || typeof document === "undefined") return;
      visible = document.visibilityState === "visible";
      recomputeCounting();
    }
    function onFocus() {
      if (destroyed) return;
      focused = true;
      recomputeCounting();
    }
    function onBlur() {
      if (destroyed) return;
      focused = false;
      recomputeCounting();
    }
    function start() {
      if (attached || destroyed || typeof window === "undefined") return;
      attached = true;
      visible = typeof document === "undefined" || document.visibilityState === "visible";
      focused = typeof document === "undefined" || typeof document.hasFocus !== "function" ? true : document.hasFocus();
      window.addEventListener("scroll", markActivity, { passive: true, capture: true });
      window.addEventListener("click", markActivity, { capture: true });
      window.addEventListener("pointerdown", markActivity, { capture: true });
      window.addEventListener("keydown", markActivity, { capture: true });
      window.addEventListener("focus", onFocus);
      window.addEventListener("blur", onBlur);
      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", onVisibilityChange);
        document.addEventListener("selectionchange", markActivity);
      }
    }
    function resetForNewView() {
      segmentStartTime = null;
      accumulatedMsec = 0;
      bumpIdleWindow();
    }
    function setConsentGranted(granted) {
      if (destroyed) return;
      consented = granted;
      recomputeCounting();
    }
    function getElapsedMsec() {
      if (segmentStartTime === null) return accumulatedMsec;
      return accumulatedMsec + Math.max(0, now() - segmentStartTime);
    }
    function getIdleTimeoutMsec() {
      return idleTimeoutMsec;
    }
    function destroy() {
      if (destroyed) return;
      closeOpenSegment();
      destroyed = true;
      if (idleTimeoutHandle !== null) {
        clearTimeout(idleTimeoutHandle);
        idleTimeoutHandle = null;
      }
      if (!attached) return;
      attached = false;
      if (typeof window !== "undefined") {
        window.removeEventListener("scroll", markActivity, { capture: true });
        window.removeEventListener("click", markActivity, { capture: true });
        window.removeEventListener("pointerdown", markActivity, { capture: true });
        window.removeEventListener("keydown", markActivity, { capture: true });
        window.removeEventListener("focus", onFocus);
        window.removeEventListener("blur", onBlur);
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
        document.removeEventListener("selectionchange", markActivity);
      }
    }
    function getDebugState() {
      return {
        visible,
        focused,
        consented,
        withinIdleWindow,
        counting: segmentStartTime !== null,
        elapsedMsec: getElapsedMsec()
      };
    }
    return {
      start,
      resetForNewView,
      setConsentGranted,
      getElapsedMsec,
      getIdleTimeoutMsec,
      destroy,
      getDebugState
    };
  }

  // src/modules/ArticleScrollDepthTracker.ts
  var DEFAULT_THRESHOLDS = [25, 50, 75, 90];
  var DEFAULT_EVENT_PREFIX = "article";
  function sanitizeEventPrefix(raw) {
    const prefix = typeof raw === "string" ? raw.trim().toLowerCase() : "";
    return /^[a-z][a-z0-9_]*$/.test(prefix) ? prefix : DEFAULT_EVENT_PREFIX;
  }
  function warn(config, ...args) {
    if (config.debug && typeof console !== "undefined") {
      console.warn("[ArticleScrollDepthTracker]", ...args);
    }
  }
  function safely(config, fn) {
    try {
      return fn();
    } catch (error) {
      warn(config, "swallowed an internal error", error);
      return void 0;
    }
  }
  function stripQueryAndFragment(url) {
    if (!url) return url;
    try {
      const base = typeof location !== "undefined" ? location.href : void 0;
      const parsed = new URL(url, base);
      return `${parsed.origin}${parsed.pathname}`;
    } catch (error) {
      return String(url).split("?")[0].split("#")[0];
    }
  }
  function resolveContainer(selector) {
    if (typeof document === "undefined" || !selector) return null;
    return document.querySelector(selector);
  }
  function deriveCanonicalUrl(configuredUrl) {
    if (configuredUrl) return stripQueryAndFragment(configuredUrl);
    if (typeof location === "undefined") return "";
    return stripQueryAndFragment(`${location.origin}${location.pathname}`);
  }
  function calculateVerticalPercentage(bounds, viewportHeight) {
    if (!(bounds.height > 0) || !(viewportHeight > 0)) return 0;
    const travel = viewportHeight + bounds.height;
    if (travel <= 0) return 0;
    const ratio = Math.max(0, Math.min(1, bounds.bottom / travel));
    return Math.round((1 - ratio) * 100);
  }
  function normalizeConfig(userConfig) {
    const raw = userConfig || {};
    const transportExplicit = raw.transport === "gtag" || raw.transport === "gtm";
    const config = {
      articleId: raw.articleId,
      containerSelector: typeof raw.containerSelector === "string" ? raw.containerSelector : null,
      canonicalUrl: raw.canonicalUrl,
      measurementId: raw.measurementId,
      // Optional labelling param. Emitted only when configured, so consumers
      // that omit it keep sending byte-identical events.
      pageType: raw.pageType || null,
      eventPrefix: sanitizeEventPrefix(raw.eventPrefix),
      transport: raw.transport === "gtm" ? "gtm" : "gtag",
      thresholds: Array.isArray(raw.thresholds) && raw.thresholds.length ? raw.thresholds.slice().sort((a, b) => a - b) : DEFAULT_THRESHOLDS.slice(),
      defaultConsentState: raw.defaultConsentState === "denied" ? "denied" : "granted",
      debug: !!raw.debug
    };
    if (!transportExplicit) {
      warn(
        config,
        `transport was not set explicitly; defaulting to "gtag". Confirm this against the site's GTM container configuration before relying on it in production.`
      );
    }
    if (!config.containerSelector) {
      warn(
        config,
        "no containerSelector provided; scroll depth will never be measured."
      );
    }
    if (!config.articleId) {
      warn(config, "no articleId provided.");
    }
    const requestedPrefix = typeof raw.eventPrefix === "string" ? raw.eventPrefix.trim().toLowerCase() : "";
    if (raw.eventPrefix && config.eventPrefix !== requestedPrefix) {
      warn(
        config,
        'eventPrefix must be alphanumeric/underscore and start with a letter; falling back to "' + DEFAULT_EVENT_PREFIX + '".',
        raw.eventPrefix
      );
    }
    return config;
  }
  function createArticleScrollDepthTracker(userConfig) {
    const config = normalizeConfig(userConfig);
    let container = null;
    let articleId = config.articleId;
    let currentCanonicalUrl;
    let reachedThresholds = /* @__PURE__ */ new Set();
    let viewEventSent = false;
    let maximumDepthEventSent = false;
    let started = false;
    let destroyed = false;
    let intersectionObserver = null;
    let resizeObserver = null;
    let isListening = false;
    let isLifecycleListening = false;
    let rafHandle = null;
    const viewSession = isArticleViewSession(
      userConfig && userConfig.viewSession
    ) ? userConfig.viewSession : createArticleViewSession();
    if (userConfig && userConfig.viewSession && !isArticleViewSession(userConfig.viewSession)) {
      warn(
        config,
        "viewSession must implement { ensureViewId, getViewId, reset }; ignoring and creating an owned session."
      );
    }
    const activeTimer = createActiveInteractionTimer();
    const gate = createConsentGate({
      defaultConsentState: config.defaultConsentState,
      onGrantedChange(after) {
        activeTimer.setConsentGranted(after);
        if (after) {
          beginTrackingIfNeeded();
        } else {
          teardownIntersectionGate();
        }
      }
    });
    function sendEvent(eventName, extraParams) {
      if (destroyed) return;
      const dataLayer = gate.getDataLayer();
      if (!dataLayer) {
        warn(config, "no dataLayer available; dropping event", eventName);
        return;
      }
      const p = config.eventPrefix;
      const fields = {
        [p + "_view_id"]: viewSession.getViewId(),
        [p + "_id"]: articleId,
        [p + "_canonical_url"]: currentCanonicalUrl,
        ...config.pageType ? { page_type: config.pageType } : {},
        ...config.containerSelector ? { scroll_container: config.containerSelector } : {},
        ...extraParams
      };
      pushDataLayerEvent({
        dataLayer,
        transport: config.transport,
        measurementId: config.measurementId,
        eventName,
        fields
      });
    }
    function recomputeReachedThresholds() {
      if (destroyed || !container || !gate.isGranted()) return;
      const bounds = container.getBoundingClientRect();
      if (!(bounds.height > 0)) return;
      const viewportHeight = typeof window !== "undefined" && window.innerHeight || 0;
      const percent = calculateVerticalPercentage(bounds, viewportHeight);
      config.thresholds.filter((threshold) => threshold <= percent).forEach((threshold) => reachedThresholds.add(threshold));
    }
    function flushMaximumScrollDepth() {
      if (destroyed || maximumDepthEventSent || !gate.isGranted() || !viewEventSent)
        return;
      recomputeReachedThresholds();
      const maximumThreshold = Array.from(reachedThresholds).sort(
        (a, b) => b - a
      )[0];
      maximumDepthEventSent = true;
      sendEvent(config.eventPrefix + "_scroll_depth", {
        percent_scrolled: maximumThreshold === void 0 ? 0 : maximumThreshold,
        // gtag honors this as an instruction to use sendBeacon during pagehide;
        // GTM consumers can forward it with their existing event settings.
        transport_type: "beacon",
        active_time_msec: Math.round(activeTimer.getElapsedMsec()),
        active_idle_timeout_msec: activeTimer.getIdleTimeoutMsec(),
        active_time_method: "momently_v1"
      });
    }
    function scheduleRecompute() {
      if (destroyed || rafHandle !== null) return;
      const schedule = typeof requestAnimationFrame === "function" ? requestAnimationFrame : (fn) => setTimeout(fn, 16);
      rafHandle = schedule(() => {
        rafHandle = null;
        safely(config, () => {
          if (destroyed) return;
          gate.reobserve();
          recomputeReachedThresholds();
        });
      });
    }
    function onPageHide() {
      safely(config, flushMaximumScrollDepth);
    }
    function attachLifecycleListener() {
      if (isLifecycleListening || destroyed || typeof window === "undefined") return;
      isLifecycleListening = true;
      window.addEventListener("pagehide", onPageHide);
    }
    function detachLifecycleListener() {
      if (!isLifecycleListening || typeof window === "undefined") return;
      isLifecycleListening = false;
      window.removeEventListener("pagehide", onPageHide);
    }
    function attachListeners() {
      if (isListening || destroyed || typeof window === "undefined") return;
      isListening = true;
      window.addEventListener("scroll", scheduleRecompute, { passive: true });
      window.addEventListener("resize", scheduleRecompute);
      if (typeof ResizeObserver === "function" && container) {
        resizeObserver = new ResizeObserver(scheduleRecompute);
        resizeObserver.observe(container);
      }
    }
    function detachListeners() {
      if (!isListening) return;
      isListening = false;
      window.removeEventListener("scroll", scheduleRecompute);
      window.removeEventListener("resize", scheduleRecompute);
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    }
    function setupIntersectionGate() {
      teardownIntersectionGate();
      if (!container) return;
      if (typeof IntersectionObserver !== "function") {
        attachListeners();
        return;
      }
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          safely(config, () => {
            const entry = entries[entries.length - 1];
            if (entry && entry.isIntersecting) {
              attachListeners();
              scheduleRecompute();
            } else {
              detachListeners();
            }
          });
        },
        { rootMargin: "100% 0px", threshold: 0 }
      );
      intersectionObserver.observe(container);
    }
    function teardownIntersectionGate() {
      if (intersectionObserver) {
        intersectionObserver.disconnect();
        intersectionObserver = null;
      }
      detachListeners();
    }
    function beginTrackingIfNeeded() {
      if (!viewEventSent) {
        viewSession.ensureViewId();
        viewEventSent = true;
        sendEvent(config.eventPrefix + "_view", {});
      }
      setupIntersectionGate();
      attachListeners();
      recomputeReachedThresholds();
    }
    function initArticleState(next) {
      teardownIntersectionGate();
      articleId = next.articleId;
      if (next.containerSelector) {
        container = resolveContainer(next.containerSelector);
        if (!container) {
          warn(
            config,
            'no element matched containerSelector "' + next.containerSelector + '"; scroll depth will never be measured.'
          );
        }
      }
      if ("canonicalUrl" in next) {
        currentCanonicalUrl = deriveCanonicalUrl(next.canonicalUrl);
      } else if (!currentCanonicalUrl) {
        currentCanonicalUrl = deriveCanonicalUrl(void 0);
      }
      reachedThresholds = /* @__PURE__ */ new Set();
      viewEventSent = false;
      maximumDepthEventSent = false;
      viewSession.reset();
      activeTimer.resetForNewView();
      if (gate.isGranted()) {
        beginTrackingIfNeeded();
      }
    }
    function start() {
      if (destroyed || started) return;
      started = true;
      safely(config, () => {
        gate.bootstrapOnce();
        activeTimer.start();
        activeTimer.setConsentGranted(gate.isGranted());
        attachLifecycleListener();
        initArticleState({
          articleId: config.articleId,
          containerSelector: config.containerSelector ?? void 0,
          canonicalUrl: config.canonicalUrl
        });
      });
    }
    function switchArticle(next) {
      if (destroyed) return;
      safely(config, () => {
        if (!started) {
          started = true;
          gate.bootstrapOnce();
          activeTimer.start();
          activeTimer.setConsentGranted(gate.isGranted());
          attachLifecycleListener();
        } else {
          flushMaximumScrollDepth();
        }
        initArticleState(next || {});
      });
    }
    function destroy() {
      if (destroyed) return;
      safely(config, flushMaximumScrollDepth);
      destroyed = true;
      safely(config, () => teardownIntersectionGate());
      safely(config, () => detachLifecycleListener());
      safely(config, () => activeTimer.destroy());
      safely(config, () => gate.teardown());
    }
    function getDebugState() {
      return safely(config, () => ({
        articleViewId: viewSession.getViewId(),
        firedThresholds: Array.from(reachedThresholds),
        maximumDepthEventSent,
        consentState: gate.getConsentState(),
        effectiveAnalyticsStorage: gate.effectiveAnalyticsStorage(),
        observingDataLayer: gate.isObserving(),
        transport: config.transport,
        eventPrefix: config.eventPrefix,
        pageType: config.pageType,
        activeTimeMsec: Math.round(activeTimer.getElapsedMsec()),
        started,
        destroyed
      })) || null;
    }
    return { start, switchArticle, destroy, getDebugState };
  }

  // src/modules/ArticleOutboundClickTracker.ts
  var EVENT_NAME = "article_outbound_click";
  function warn2(config, ...args) {
    if (config.debug && typeof console !== "undefined") {
      console.warn("[ArticleOutboundClickTracker]", ...args);
    }
  }
  function safely2(config, fn) {
    try {
      return fn();
    } catch (error) {
      warn2(config, "swallowed an internal error", error);
      return void 0;
    }
  }
  function resolveContainer2(selector) {
    if (typeof document === "undefined" || !selector) return null;
    return document.querySelector(selector);
  }
  function stripQueryAndFragment2(url) {
    if (!url) return url;
    try {
      const base = typeof location !== "undefined" ? location.href : void 0;
      const parsed = new URL(url, base);
      return `${parsed.origin}${parsed.pathname}`;
    } catch (error) {
      return String(url).split("?")[0].split("#")[0];
    }
  }
  function deriveCanonicalUrl2(configuredUrl) {
    if (configuredUrl) return stripQueryAndFragment2(configuredUrl);
    if (typeof location === "undefined") return "";
    return stripQueryAndFragment2(`${location.origin}${location.pathname}`);
  }
  function normalizeConfig2(userConfig) {
    const raw = userConfig || {};
    const transportExplicit = raw.transport === "gtag" || raw.transport === "gtm";
    const config = {
      containerSelector: typeof raw.containerSelector === "string" ? raw.containerSelector : null,
      excludeLinkSelector: typeof raw.excludeLinkSelector === "string" ? raw.excludeLinkSelector : null,
      internalDomains: Array.isArray(raw.internalDomains) ? raw.internalDomains.filter((d) => typeof d === "string" && d) : [],
      articleId: raw.articleId,
      canonicalUrl: raw.canonicalUrl,
      measurementId: raw.measurementId,
      pageType: raw.pageType || null,
      transport: raw.transport === "gtm" ? "gtm" : "gtag",
      defaultConsentState: raw.defaultConsentState === "denied" ? "denied" : "granted",
      debug: !!raw.debug
    };
    if (!transportExplicit) {
      warn2(
        config,
        `transport was not set explicitly; defaulting to "gtag". Confirm this against the site's GTM container configuration before relying on it in production.`
      );
    }
    if (!config.containerSelector) {
      warn2(
        config,
        "no containerSelector provided; outbound clicks will never be measured."
      );
    }
    return config;
  }
  function createArticleOutboundClickTracker(userConfig) {
    const config = normalizeConfig2(userConfig);
    let container = null;
    let listenerContainer = null;
    let isListening = false;
    let articleId = config.articleId;
    let currentCanonicalUrl;
    let started = false;
    let destroyed = false;
    const ownsSession = !isArticleViewSession(userConfig && userConfig.viewSession);
    const viewSession = ownsSession ? createArticleViewSession() : userConfig.viewSession;
    if (userConfig && userConfig.viewSession && ownsSession) {
      warn2(
        config,
        "viewSession must implement { ensureViewId, getViewId, reset }; ignoring and creating an owned session."
      );
    }
    const gate = createConsentGate({
      defaultConsentState: config.defaultConsentState,
      onGrantedChange(after) {
        if (after) {
          beginTrackingIfNeeded();
        } else {
          detachClickListener();
        }
      }
    });
    function isInternalHostname(hostname) {
      const pageHost = typeof location !== "undefined" && location.hostname || "";
      const normalizedPageHost = pageHost.toLowerCase();
      if (hostname === normalizedPageHost) return true;
      return config.internalDomains.some((raw) => {
        const domain = raw.toLowerCase();
        return hostname === domain || hostname.endsWith("." + domain);
      });
    }
    function sendEvent(extraFields) {
      if (destroyed) return;
      const dataLayer = gate.getDataLayer();
      if (!dataLayer) {
        warn2(config, "no dataLayer available; dropping event", EVENT_NAME);
        return;
      }
      const viewId = viewSession.getViewId();
      if (!viewId) {
        warn2(
          config,
          "dropping " + EVENT_NAME + ": no article_view_id established yet"
        );
        return;
      }
      const fields = {
        article_view_id: viewId,
        ...articleId ? { article_id: articleId } : {},
        ...currentCanonicalUrl ? { article_canonical_url: currentCanonicalUrl } : {},
        ...config.pageType ? { page_type: config.pageType } : {},
        outbound: 1,
        ...typeof location !== "undefined" ? { page_location: location.href } : {},
        ...extraFields
      };
      pushDataLayerEvent({
        dataLayer,
        transport: config.transport,
        measurementId: config.measurementId,
        eventName: EVENT_NAME,
        fields
      });
    }
    function handleClick(event) {
      if (destroyed || !gate.isGranted()) return;
      const target = event.target;
      const anchor = target && typeof target.closest === "function" ? target.closest("a[href]") : null;
      const currentTarget = event.currentTarget;
      if (!anchor || !currentTarget || !currentTarget.contains(anchor)) return;
      if (config.excludeLinkSelector && anchor.closest(config.excludeLinkSelector)) {
        return;
      }
      if (anchor.protocol !== "http:" && anchor.protocol !== "https:") return;
      const hostname = anchor.hostname.toLowerCase();
      if (isInternalHostname(hostname)) return;
      const linkDomain = hostname.replace(/^www\./, "");
      sendEvent({ link_url: anchor.href, link_domain: linkDomain });
    }
    function attachClickListener() {
      if (destroyed || !container) return;
      if (isListening && listenerContainer === container) return;
      detachClickListener();
      container.addEventListener("click", handleClick);
      listenerContainer = container;
      isListening = true;
    }
    function detachClickListener() {
      if (!isListening || !listenerContainer) return;
      listenerContainer.removeEventListener("click", handleClick);
      listenerContainer = null;
      isListening = false;
    }
    function beginTrackingIfNeeded() {
      if (ownsSession) viewSession.ensureViewId();
      attachClickListener();
    }
    function initArticleState(next) {
      detachClickListener();
      if (next.containerSelector) {
        container = resolveContainer2(next.containerSelector);
        if (!container) {
          warn2(
            config,
            'no element matched containerSelector "' + next.containerSelector + '"; outbound clicks will never be measured.'
          );
        }
      }
      articleId = next.articleId;
      if ("canonicalUrl" in next) {
        currentCanonicalUrl = deriveCanonicalUrl2(next.canonicalUrl);
      } else if (!currentCanonicalUrl) {
        currentCanonicalUrl = deriveCanonicalUrl2(void 0);
      }
      if (ownsSession) viewSession.reset();
      if (gate.isGranted()) {
        beginTrackingIfNeeded();
      }
    }
    function start() {
      if (destroyed || started) return;
      started = true;
      safely2(config, () => {
        gate.bootstrapOnce();
        initArticleState({
          articleId: config.articleId,
          containerSelector: config.containerSelector ?? void 0,
          canonicalUrl: config.canonicalUrl
        });
      });
    }
    function switchArticle(next) {
      if (destroyed) return;
      safely2(config, () => {
        if (!started) {
          started = true;
          gate.bootstrapOnce();
        }
        initArticleState(next || {});
      });
    }
    function destroy() {
      if (destroyed) return;
      destroyed = true;
      safely2(config, () => detachClickListener());
      safely2(config, () => gate.teardown());
    }
    function getDebugState() {
      return safely2(config, () => ({
        articleViewId: viewSession.getViewId(),
        consentState: gate.getConsentState(),
        effectiveAnalyticsStorage: gate.effectiveAnalyticsStorage(),
        observingDataLayer: gate.isObserving(),
        transport: config.transport,
        containerSelector: config.containerSelector,
        excludeLinkSelector: config.excludeLinkSelector,
        internalDomains: config.internalDomains,
        pageType: config.pageType,
        started,
        destroyed
      })) || null;
    }
    return { start, switchArticle, destroy, getDebugState };
  }

  // src/createArticleTracking.ts
  var MODULE_REGISTRY = {
    scrollDepth: {
      factory: createArticleScrollDepthTracker,
      generatesViewId: true
    },
    outboundClick: {
      factory: createArticleOutboundClickTracker,
      generatesViewId: false
    }
  };
  var SHARED_KEYS = [
    "articleId",
    "canonicalUrl",
    "containerSelector",
    "transport",
    "measurementId",
    "pageType",
    "defaultConsentState",
    "debug"
  ];
  function warn3(cfg, ...args) {
    if (cfg.debug && typeof console !== "undefined") {
      console.warn("[createArticleTracking]", ...args);
    }
  }
  function safely3(cfg, label, fn) {
    try {
      return fn();
    } catch (error) {
      warn3(cfg, `module "${label}" threw`, error);
      return void 0;
    }
  }
  function isModuleEnabled(value) {
    return typeof value === "object" && value !== null;
  }
  function pick(source, keys) {
    const result = {};
    keys.forEach((key) => {
      if (key in source) result[key] = source[key];
    });
    return result;
  }
  function createArticleTracking(userConfig) {
    const raw = userConfig || {};
    const sharedConfig = pick(
      raw,
      SHARED_KEYS
    );
    const modulesConfig = typeof raw.modules === "object" && raw.modules !== null ? raw.modules : {};
    Object.keys(raw).forEach((key) => {
      if (key === "modules" || SHARED_KEYS.includes(key)) return;
      warn3(
        sharedConfig,
        `unknown config key "${key}" -- ignoring. Expected a shared field (${SHARED_KEYS.join(", ")}) or "modules".`
      );
    });
    const registryKeys = Object.keys(MODULE_REGISTRY);
    const enabledKeys = [];
    Object.keys(modulesConfig).forEach((key) => {
      if (!registryKeys.includes(key)) {
        warn3(
          sharedConfig,
          `unknown module "${key}" -- ignoring. Available modules: ${registryKeys.join(", ")}.`
        );
        return;
      }
      const moduleValue = modulesConfig[key];
      if (!isModuleEnabled(moduleValue)) {
        if (moduleValue) {
          warn3(
            sharedConfig,
            `module "${key}" config must be an object (even {}) to enable it; received a non-object truthy value -- treating as disabled.`
          );
        }
        return;
      }
      enabledKeys.push(key);
    });
    if (enabledKeys.length === 0) {
      warn3(
        sharedConfig,
        `no modules enabled. Available modules: ${registryKeys.join(", ")}.`
      );
    }
    const hasGenerator = enabledKeys.some(
      (key) => MODULE_REGISTRY[key].generatesViewId
    );
    const viewSession = hasGenerator ? createArticleViewSession() : null;
    const enabledEntries = enabledKeys.map((key) => {
      const overrides = modulesConfig[key];
      if ("viewSession" in overrides) {
        warn3(
          sharedConfig,
          `module "${key}" config included "viewSession"; createArticleTracking manages viewSession internally -- ignoring it.`
        );
      }
      const merged = { ...sharedConfig, ...overrides };
      delete merged.viewSession;
      if (viewSession) merged.viewSession = viewSession;
      const instance = MODULE_REGISTRY[key].factory(merged);
      return { key, instance };
    });
    let started = false;
    let destroyed = false;
    function start() {
      if (destroyed || started) return;
      started = true;
      enabledEntries.forEach(
        ({ key, instance }) => safely3(sharedConfig, key, () => instance.start())
      );
    }
    function switchArticle(next) {
      if (destroyed) return;
      const payload = { ...next || {} };
      enabledEntries.forEach(
        ({ key, instance }) => safely3(sharedConfig, key, () => instance.switchArticle(payload))
      );
    }
    function destroy() {
      if (destroyed) return;
      destroyed = true;
      enabledEntries.forEach(
        ({ key, instance }) => safely3(sharedConfig, key, () => instance.destroy())
      );
    }
    function getDebugState() {
      const state = {
        articleViewId: viewSession ? viewSession.getViewId() : null,
        enabledModules: enabledEntries.map(({ key }) => key)
      };
      enabledEntries.forEach(({ key, instance }) => {
        state[key] = safely3(sharedConfig, key, () => instance.getDebugState());
      });
      return state;
    }
    return { start, switchArticle, destroy, getDebugState };
  }
  return __toCommonJS(index_exports);
})();
